import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./DashboardLayout.css";

interface DashboardLayoutProps {
  children: React.ReactNode;
  pageTitle: string;
  activePath: string;
}

const NAV_ITEMS = [
  {
    label: "Main",
    links: [
      { to: "/dashboard",   icon: "chart",    text: "Dashboard"       },
      { to: "/profile",     icon: "user",     text: "Profile"         },
      { to: "/tests",       icon: "clipboard",text: "My Tests"        },
    ],
  },
  {
    label: "Content",
    links: [
      { to: "/online-test-series", icon: "book",  text: "Test Series"   },
      { to: "/blogs/all-blogs/mcqs", icon: "brain", text: "MCQ Practice" },
    ],
  },
  {
    label: "Account",
    links: [
      { to: "/change-password", icon: "key",    text: "Change Password" },
    ],
  },
];

const ICONS: Record<string, React.ReactNode> = {
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="18" y="3" width="4" height="18"/><rect x="10" y="8" width="4" height="13"/>
      <rect x="2" y="13" width="4" height="8"/>
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
  brain: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.44-4.14Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.44-4.14Z"/>
    </svg>
  ),
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="6"  x2="21" y2="6"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, pageTitle, activePath }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName]       = useState("");
  const [userEmail, setUserEmail]     = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) {
      const u = JSON.parse(raw);
      setUserName(`${u.firstname || ""} ${u.lastname || ""}`.trim());
      setUserEmail(u.email || "");
    }
  }, []);

  const initials = userName
    ? userName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : "A";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
    window.location.reload();
  };

  const active = activePath || location.pathname;

  return (
    <div className="dsh-root">
      {/* ── Sidebar ── */}
      <nav className={`dsh-sidebar${sidebarOpen ? " open" : ""}`}>
        {/* Brand */}
        <Link to="/" className="dsh-sidebar-brand" onClick={() => setSidebarOpen(false)}>
          <div className="dsh-brand-mark">A</div>
          <span className="dsh-brand-name">acade<span>bility</span></span>
        </Link>

        {/* User mini-card */}
        {userName && (
          <div className="dsh-sidebar-user">
            <div className="dsh-user-avatar">{initials}</div>
            <div>
              <div className="dsh-user-name">{userName}</div>
              <div className="dsh-user-email">{userEmail}</div>
            </div>
          </div>
        )}

        {/* Nav groups */}
        <div className="dsh-nav">
          {NAV_ITEMS.map(group => (
            <React.Fragment key={group.label}>
              <div className="dsh-nav-label">{group.label}</div>
              {group.links.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`dsh-nav-link${active === link.to ? " active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="dsh-nav-icon">{ICONS[link.icon]}</span>
                  {link.text}
                </Link>
              ))}
            </React.Fragment>
          ))}
        </div>

        {/* Logout */}
        <div className="dsh-sidebar-footer">
          <button className="dsh-logout-btn" onClick={handleLogout}>
            {ICONS.logout}
            Sign out
          </button>
        </div>
      </nav>

      {/* ── Mobile overlay ── */}
      <div
        className={`dsh-overlay${sidebarOpen ? " show" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Main area ── */}
      <div className="dsh-main">
        {/* Top bar */}
        <header className="dsh-topbar">
          <div className="dsh-topbar-left">
            <button className="dsh-mobile-toggle" onClick={() => setSidebarOpen(o => !o)}>
              {ICONS.menu}
            </button>
            <div>
              <div className="dsh-topbar-title">{pageTitle}</div>
              <div className="dsh-topbar-breadcrumb">Home / {pageTitle}</div>
            </div>
          </div>
          <div className="dsh-topbar-right">
            <div className="dsh-topbar-avatar" title={userName}>{initials}</div>
          </div>
        </header>

        {/* Page content */}
        <div className="dsh-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
